import * as Analytics from 'analytics/analytics'
import { clientEvent } from 'analytics/snowplow/events'
import { reportUserTimingEvent } from 'lib/performance'
import { useCallback } from 'react'

interface ExperienceRecord {
  start: number;
}

interface UserTimingProperties {
  eventCategory: string;
  variable: string;
  label: string;
}

export type PerformanceExperience = 'lereLPPRenderReady' |
  'lereCalendarRender' |
  'lereFlashRenderReady' |
  'lereBundleAndSaveRenderReady' |
  'lereAlwaysOnRenderReady' |
  'lereHotelSearchRenderReady' |
  'lereTPFMAbandonedCartsSectionRenderReady' |
  'lereTPFMAlternativeOffersSectionRenderReady' |
  'lereTPFMPersonalisedContextSectionRenderReady' |
  'lereTPFMTopDealsRenderReady' |
  'lereTPFMAllSections' |
  'lereTPFMPeopleLikeMe';

const _experiences: { [key in PerformanceExperience]?: ExperienceRecord } = {}

function usePerformanceTracker() {
  const start = useCallback((experience: PerformanceExperience) => {
    if (_experiences[experience] === undefined) {
      _experiences[experience] = { start: Date.now() }
    }
  }, [])

  const succeed = useCallback((experience: PerformanceExperience, properties: UserTimingProperties) => {
    if (!_experiences[experience]) {
      return
    }
    const duration = Date.now() - _experiences[experience].start

    reportUserTimingEvent({
      eventCategory: properties.eventCategory,
      variable: properties.variable,
      value: duration,
      label: properties.label,
    })

    Analytics.track('client-event', clientEvent(
      `${properties.eventCategory}__${properties.variable}__${properties.label}`,
      `${duration}`,
      properties.eventCategory,
      'operational',
    ))

    delete _experiences[experience]
  }, [])

  const cancel = useCallback((experience: PerformanceExperience, gaProperties?: UserTimingProperties) => {
    if (!_experiences[experience]) {
      return
    }
    const duration = Date.now() - _experiences[experience].start

    // Sometimes you want to cancel without reporting an analytic event.
    if (gaProperties) {
      reportUserTimingEvent({
        eventCategory: gaProperties.eventCategory,
        variable: gaProperties.variable,
        value: duration,
        label: gaProperties.label,
      })
    }

    delete _experiences[experience]
  }, [])

  const fail = useCallback((experience: PerformanceExperience, gaProperties: UserTimingProperties) => {
    if (!_experiences[experience]) {
      return
    }
    const duration = Date.now() - _experiences[experience].start

    reportUserTimingEvent({
      eventCategory: gaProperties.eventCategory,
      variable: gaProperties.variable,
      value: duration,
      label: gaProperties.label,
    })

    delete _experiences[experience]
  }, [])

  return {
    start,
    succeed,
    cancel,
    fail,
  }
}

export default usePerformanceTracker
